import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type {
  CreateEstimationItemRequest,
  RenameEstimationItemRequest,
  UpdateEstimationItemRequest,
  UpsertEstimationItemsRequest,
} from '../../schema/data-contracts';
import { estimationItemAssumptionsKey } from '../estimationItemAssumptionQueryActions';
import type { MutationProps } from '../types';

export const GetEstimationItemKey = 'estimation-item';
export const GetEstimationItemsByEstimationKey = 'estimation-items-estimationId';

export const useEstimationItemQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationItemController: {
      v1EstimationsEstimationItemsCreate,
      v1EstimationItemsDetail,
      v1EstimationItemsUpdate,
      v1EstimationItemsNamePartialUpdate,
      v1EstimationsEstimationItemsPartialUpdate,
      v1EstimationItemsDelete,
      v1EstimationsEstimationItemsList,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetEstimationItem = (estimationItemId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationItemId,
      queryKey: [GetEstimationItemKey, 'estimationItemId', estimationItemId],
      queryFn: async () => {
        const { data } = await v1EstimationItemsDetail(estimationItemId!);
        return data;
      },
    });

  const useGetEstimationItemsByEstimationId = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId),
      queryKey: [GetEstimationItemsByEstimationKey, estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsEstimationItemsList(estimationId!);
        return data;
      },
    });

  const useCreateEstimationItem = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-create'],
      mutationFn: async ({
        estimationId,
        estimationItem,
      }: {
        estimationId: string;
        estimationItem: CreateEstimationItemRequest;
      }) => {
        const { data } = await v1EstimationsEstimationItemsCreate(estimationId, estimationItem);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [GetEstimationItemsByEstimationKey, variables.estimationId],
        });
      },
    });

  const useCreateEstimationItems = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-items-create'],
      mutationFn: async ({
        estimationId,
        itemsList,
      }: {
        estimationId: string;
        itemsList: CreateEstimationItemRequest[];
      }) => {
        const createdEstimationItemsList = await Promise.all(
          itemsList.map(async (item) => {
            const { data } = await v1EstimationsEstimationItemsCreate(estimationId, item);
            return data;
          }),
        );
        return createdEstimationItemsList;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [GetEstimationItemsByEstimationKey, variables.estimationId],
        });
      },
    });

  const useUpdateEstimationItem = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-update'],
      mutationFn: async ({
        estimationItemId,
        estimationItem,
      }: {
        estimationItemId: string;
        estimationItem: UpdateEstimationItemRequest;
      }) => {
        const { data } = await v1EstimationItemsUpdate(estimationItemId, estimationItem);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            (query.queryKey[0] === GetEstimationItemKey &&
              query.queryKey[1] === variables.estimationItemId) ||
            query.queryKey[0] === GetEstimationItemsByEstimationKey,
        });
      },
    });

  const useRenameEstimationItem = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-rename'],
      mutationFn: async ({
        estimationItemId,
        name,
      }: {
        estimationItemId: string;
        name: string;
      }) => {
        const payload: RenameEstimationItemRequest = { name };
        const { data } = await v1EstimationItemsNamePartialUpdate(estimationItemId, payload);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            (query.queryKey[0] === GetEstimationItemKey &&
              query.queryKey[1] === variables.estimationItemId) ||
            query.queryKey[0] === GetEstimationItemsByEstimationKey,
        });
      },
    });

  const usePatchEstimationItems = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-update'],
      mutationFn: async ({
        estimationId,
        payload,
      }: {
        estimationId: string;
        payload: UpsertEstimationItemsRequest;
      }) => {
        const { data } = await v1EstimationsEstimationItemsPartialUpdate(estimationId, payload);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            (query.queryKey[0] === GetEstimationItemKey &&
              typeof query.queryKey[1] === 'string' &&
              variables.payload.estimationItemsToUpdate
                .map((x) => x.id)
                .includes(query.queryKey[1])) ||
            (query.queryKey[0] === GetEstimationItemsByEstimationKey &&
              query.queryKey[1] === variables.estimationId),
        });
        // TODO: should we remove deleted estimation items from cache?
      },
    });

  const useDeleteEstimationItem = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-delete'],
      mutationFn: async (variables: { estimationItemId: string }) => {
        const { data } = await v1EstimationItemsDelete(variables.estimationItemId);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === GetEstimationItemKey ||
            query.queryKey[0] === GetEstimationItemsByEstimationKey ||
            query.queryKey[0] === estimationItemAssumptionsKey,
        });
      },
    });

  return {
    useGetEstimationItem,
    useCreateEstimationItem,
    useUpdateEstimationItem,
    useRenameEstimationItem,
    usePatchEstimationItems,
    useDeleteEstimationItem,
    useCreateEstimationItems,
    useGetEstimationItemsByEstimationId,
  };
};
